import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import { rem } from 'polished'
import type { MouseEvent, ReactElement, ReactNode, TouchEvent } from 'react'
import { useCallback, useRef } from 'react'
import styled from 'styled-components'

type OptionCardVariant = 'default' | 'highlighted'

const OptionCardsContainer = styled.div<{ $hasSecondaryOffer: boolean }>`
  display: grid;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  gap: ${rem(16)};
  grid-auto-flow: column;
  grid-auto-columns: ${props => props.$hasSecondaryOffer ? `minmax(${rem(270)}, 85%)` : '1fr'};
  margin-top: ${rem(-14)};
  overflow-x: auto;
  padding-top: ${rem(14)};
  overscroll-behavior-x: contain;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;

  > * {
    scroll-snap-align: start;
  }
`

const OptionCard = styled(Clickable)<{ $variant: OptionCardVariant }>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${rem(16)};
  border: ${props => props.$variant === 'highlighted' ? rem(2) : rem(1)} solid ${props => props.$variant === 'highlighted' ? props.theme.palette.product.exclusive.background : props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};
  padding: ${rem(16)};
  min-height: ${rem(310)};
  position: relative;
  text-decoration: none;
  color: inherit;
`

const OptionCardLabel = styled.div`
  position: absolute;
  top: 0;
  inset-inline-start: ${rem(-2)};
  transform: translateY(-50%);
  z-index: 1;
`

const OptionCardSkeleton = styled(Group)`
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};
  padding: ${rem(16)};
  min-height: ${rem(310)};
`

const OptionCardContent = styled.div`
  flex: 1;
`

const OptionCardTitle = styled(Heading)`
  && {
    font-size: ${rem(16)};
    line-height: ${rem(20)};
  }
`

const FullWidthLoadingBox = styled(LoadingBox)`
  width: 100%;
`

const UnavailableText = styled.p`
  margin: 0;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  font-size: ${rem(14)};
  line-height: ${rem(20)};

  .unavailable-text {
    color: ${props => props.theme.palette.messaging.warning.darkForeground};
    font-weight: ${props => props.theme.font.primary.weight.superBold};
  }

  .price-text {
    font-weight: ${props => props.theme.font.primary.weight.superBold};
    margin-inline-start: ${rem(4)};
  }

  .price-unit-text {
    margin-inline-start: ${rem(4)};
  }
`

interface OptionCardsProps {
  children: ReactNode
  hasSecondaryOffer: boolean
}

interface OptionCardProps {
  title: string
  features?: ReactElement | false
  pricePoint?: ReactElement | false
  actionLabel: string
  isPrimary?: boolean
  label?: ReactNode
  variant?: OptionCardVariant
  to?: string
  target?: string
  onClick?: (event: MouseEvent<HTMLElement>) => void
}

interface UnavailableOptionCardProps {
  title: string
  features?: ReactElement | false
  unavailableLabel: string
  departureLabel?: string
  price?: number
  priceUnitLabel: string
  actionLabel: string
  to?: string
  target?: string
  onClick?: (event: MouseEvent<HTMLElement>) => void
}

// Ignore tiny touch jitter so taps still click, while real horizontal swipes do not navigate.
const SWIPE_CLICK_TOLERANCE = 8

export function GroupedHotelSearchTileOptionCards({
  children,
  hasSecondaryOffer,
}: OptionCardsProps) {
  const touchStartXRef = useRef(0)
  const touchStartYRef = useRef(0)
  const hasSwipedRef = useRef(false)

  const handleTouchStart = useCallback((event: TouchEvent<HTMLDivElement>) => {
    touchStartXRef.current = event.touches[0].clientX
    touchStartYRef.current = event.touches[0].clientY
    hasSwipedRef.current = false
  }, [])

  const handleTouchMove = useCallback((event: TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0]
    const moveX = Math.abs(touch.clientX - touchStartXRef.current)
    const moveY = Math.abs(touch.clientY - touchStartYRef.current)

    hasSwipedRef.current = moveX > SWIPE_CLICK_TOLERANCE && moveX > moveY
  }, [])

  const handleClickCapture = useCallback((event: MouseEvent<HTMLDivElement>) => {
    if (!hasSwipedRef.current) return

    event.preventDefault()
    event.stopPropagation()
    hasSwipedRef.current = false
  }, [])

  return <OptionCardsContainer
    $hasSecondaryOffer={hasSecondaryOffer}
    onTouchStart={handleTouchStart}
    onTouchMove={handleTouchMove}
    onClickCapture={handleClickCapture}
  >
    {children}
  </OptionCardsContainer>
}

export function GroupedHotelSearchTileOptionCard({
  title,
  features,
  pricePoint,
  actionLabel,
  isPrimary = false,
  label,
  variant = 'default',
  to,
  target,
  onClick,
}: OptionCardProps) {
  return <OptionCard
    $variant={variant}
    to={to}
    target={target}
    onClick={onClick}
  >
    {!!label && <OptionCardLabel>{label}</OptionCardLabel>}
    <OptionCardContent>
      <Group direction="vertical" gap={12}>
        <OptionCardTitle variant="heading5">
          {title}
        </OptionCardTitle>
        {features}
      </Group>
    </OptionCardContent>
    {pricePoint}
    <TextButton
      kind="primary"
      fit="full-width"
      nonInteractable={isPrimary}
    >
      {actionLabel}
    </TextButton>
  </OptionCard>
}

export function GroupedHotelSearchTileOptionCardSkeleton() {
  return <OptionCardSkeleton direction="vertical" gap={16} horizontalAlign="stretch">
    <OptionCardContent>
      <Group direction="vertical" gap={12}>
        <Group direction="vertical" gap={2}>
          <TextLoadingBox typography="heading5" width="70%" />
          <TextLoadingBox typography="body-medium" width="45%" />
        </Group>
        <Group direction="vertical" gap={8}>
          <TextLoadingBox typography="body-small" width="80%" />
          <TextLoadingBox typography="body-small" width="65%" />
          <TextLoadingBox typography="body-small" width="50%" />
        </Group>
      </Group>
    </OptionCardContent>
    <Group direction="vertical" gap={8}>
      <TextLoadingBox typography="body-small" width="40%" />
      <TextLoadingBox typography="heading4" width="60%" />
    </Group>
    <FullWidthLoadingBox height={48} borderRadius="S" />
  </OptionCardSkeleton>
}

export function GroupedHotelSearchTileUnavailableOptionCard({
  title,
  features,
  unavailableLabel,
  departureLabel,
  price,
  priceUnitLabel,
  actionLabel,
  to,
  target,
  onClick,
}: UnavailableOptionCardProps) {
  return <OptionCard
    $variant="default"
    to={to}
    target={target}
    onClick={onClick}
  >
    <OptionCardContent>
      <Group direction="vertical" gap={12}>
        <OptionCardTitle variant="heading5">
          {title}
        </OptionCardTitle>
        {features}
      </Group>
    </OptionCardContent>
    <UnavailableText>
      <span className="unavailable-text">
        {unavailableLabel}
      </span>
      {!!departureLabel && !!price && <>
        <br />
        {departureLabel}
        <span className="price-text">
          <FormatCurrency value={price} format="roundedDollar" />
        </span>
        <span className="price-unit-text">
          {priceUnitLabel}
        </span>
      </>}
    </UnavailableText>
    <TextButton
      kind="primary"
      fit="full-width"
      nonInteractable
    >
      {actionLabel}
    </TextButton>
  </OptionCard>
}
